import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { XMarkIcon, CurrencyDollarIcon, ReceiptPercentIcon } from '@heroicons/react/24/outline'
import { Input } from '../ui'
import {
  getCombinedStockDisplayOptions,
  getProductDetailDisplayStock
} from '../../utils/productStockDisplayUtils'
import { getLineItemIvaRateFromDetails } from '../../utils/orderFormUtils'
import { normalizeTaxRatePercent } from '../../utils/productTaxRateUtils'
import type { LineItem as OrderLineItem } from '../../types/orderFormTypes'
interface LineItem {
  product_id: string;
  sku?: string;
  product_name: string; 
  quantity: number; 
  price: string; 
  iva?: string; 
  stock?: number; 
  discount?: number; 
  name?: string; 
  is_combo?: number; 
  display_sku?: string;
  components?: Array<{
    id_combo?: number; sku: string; quantity: number; name: string; price: string; tax_rate: string;
    ean12?: string; peso?: string | null; longitud?: string | null; ancho?: string | null; alto?: string | null;
    instrucciones_empaque?: string; imagen?: string;
  }>;
}
interface OrderItemsTableProps {
  lineItems: LineItem[]; productDetails: {[key: string]: any}; loadingDetails: {[key: string]: boolean};
  expandedCombos: {[key: string]: boolean}; mode: 'view' | 'edit' | 'create'; formDataStatus?: string;
  onQuantityChange: (index: number, quantity: number) => void;
  onRemoveProduct: (index: number) => void;
  onDiscountChange: (index: number, discount: number) => void;
  onToggleCombo: (productId: string) => void;
  calculateTotals: (items: LineItem[], shipping?: string) => any;
  shippingCost?: string;
  onPriceChange: (index: number, price: number) => void;
  allowPriceEdit?: boolean;
  allowDiscountEdit?: boolean;
  clienteNeto?: boolean;
  /** Si true, calculateTotals ya incluye envío en total (cotizaciones) */
  shippingIncludedInCalculateTotals?: boolean;
}

const OrderItemsTable: React.FC<OrderItemsTableProps> = ({
  lineItems, productDetails, loadingDetails, expandedCombos,
  mode, formDataStatus,
  onQuantityChange,
  onRemoveProduct,
  onDiscountChange,
  onToggleCombo,
  calculateTotals,
  shippingCost = '0',
  onPriceChange,
  allowPriceEdit = false,
  allowDiscountEdit = true,
  clienteNeto = false,
  shippingIncludedInCalculateTotals = false
}) => {
  const stockDisplayOptions = useMemo(() => getCombinedStockDisplayOptions(), [])

  const getLineStock = useCallback(
    (detail: Record<string, unknown> | undefined) =>
      getProductDetailDisplayStock(detail, stockDisplayOptions),
    [stockDisplayOptions]
  )

  // Estado para controlar la validación de stock
  const [stockValidation, setStockValidation] = useState({
    isValid: true,
    hasValidated: false
  });

  // Estado local para el tipo de descuento por item (solo visual)
  const [discountTypes, setDiscountTypes] = useState<{[key: string]: 'currency' | 'percentage'}>({});

  const validateStockAvailability = useCallback(() => {
    if (mode === 'create') {
      if (!lineItems?.length) return true;
      return lineItems.every(item => {
        const productDetail = productDetails[item.sku || item.product_id]
        return getLineStock(productDetail) >= item.quantity
      })
    }
    if (mode === 'edit') {
      const estadosQueVerificanStock = ['processing', 'pending','send_later','verify_transaction'];
      if (!estadosQueVerificanStock.includes(formDataStatus || '')) {
        return true;
      }
      
      if (!lineItems?.length) return true;
      
      return lineItems.every(item => {
        const productDetail = productDetails[item.sku || item.product_id]
        return getLineStock(productDetail) >= item.quantity
      })
    }
    return true;
  }, [mode, formDataStatus, lineItems, productDetails, getLineStock]);

  // Validar stock solo después de que la API traiga los datos
  useEffect(() => {
    // Solo validar si tenemos datos de productos y detalles de stock
    if (productDetails && Object.keys(productDetails).length > 0 && lineItems.length > 0) {
      const isValid = validateStockAvailability();
      setStockValidation({
        isValid,
        hasValidated: true
      });
    }
  }, [productDetails, lineItems, mode, formDataStatus, validateStockAvailability]);

  const calculateTotalsWithShipping = (items: LineItem[]) => {
    const shippingStr = shippingCost || '0'
    const shipping = parseFloat(shippingStr)
    const totals = calculateTotals(items, shippingStr)
    if (shippingIncludedInCalculateTotals) {
      return {
        ...totals,
        shipping: (totals.shipping ?? shippingStr).toString(),
        total: totals.total
      }
    }
    const totalWithShipping = parseFloat(totals.total) + shipping
    return {
      ...totals,
      shipping: shipping.toFixed(2),
      total: totalWithShipping.toFixed(2)
    }
  }

  const truncateText = (text: string | undefined | null, maxLength: number) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return (
      <span className="group relative">
        {text.substring(0, maxLength)}
        <span className="cursor-help">...</span>
        <span className="absolute left-0 top-full z-10 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-1 whitespace-nowrap">
          {text}
        </span>
      </span>
    );
  };

  // Obtener SKUs de productos con stock insuficiente
  const getProductsWithInsufficientStock = useCallback(() => {
    if (!lineItems?.length || !productDetails) return [];
    
    const shouldValidate = mode === 'create' || 
      (mode === 'edit' && ['processing', 'pending', 'send_later', 'verify_transaction'].includes(formDataStatus || ''));
    
    if (!shouldValidate) return [];
    
    return lineItems
      .filter(item => {
        const productDetail = productDetails[item.sku || item.product_id]
        const stock = getLineStock(productDetail)
        return stock < item.quantity
      })
      .map(item => item.sku || item.display_sku || item.product_id);
  }, [lineItems, productDetails, mode, formDataStatus, getLineStock]);

  if (!lineItems || lineItems.length === 0) {
    return (
      <div className="bg-gray-50 p-8 text-center rounded-lg">
        <p className="text-gray-500 text-lg">No hay productos agregados</p>
        <p className="text-gray-400 text-sm mt-2">Agrega productos usando el buscador de arriba</p>
      </div>
    );
  }

  const isStockAvailable = stockValidation.isValid;
  const affectedSkus = getProductsWithInsufficientStock();
  
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden w-full">
        <div className="">
        {stockValidation.hasValidated && !isStockAvailable && mode !== 'view' && lineItems.length > 0 && (
          <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800 text-sm">
              ⚠️ Algunos productos no tienen stock para la cantidad solicitada
              {affectedSkus.length > 0 && (
                <span className="ml-2 font-semibold">
                  ({affectedSkus.join(', ')})
                </span>
              )}
            </p>
          </div>
        )}
      </div>
      <div className="w-full overflow-x-auto">
        <table className="w-full divide-y divide-gray-200">
          <thead className="bg-custom-blue h-2">
            <tr>
              <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">SKU</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Nombre</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Stock</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Cantidad</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Precio</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">IVA</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Descuento</th>
              <th className="px-2 py-2 text-left text-xs font-medium text-white uppercase tracking-wider">Total</th>
              {(mode === 'create' || mode === 'edit') && (
                <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider"></th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {lineItems.map((item, index) => {
              //const productDetail = productDetails[item.product_id] || {};
              const productDetail = productDetails[item.sku || ''] || productDetails[item.product_id] || {};
              const stockValue = getLineStock(productDetail)
              const taxRateRaw = productDetail.iva_ofima ?? productDetail.tax_rate ?? productDetail.iva ?? item.iva;
              const ivaRateDecimal = getLineItemIvaRateFromDetails(item as OrderLineItem, productDetails);
              const isCombo = item.is_combo === 1;
              const components = item.components || [];
              const isExpanded = expandedCombos[item.product_id];

              const mainRow = (
                <tr key={item.product_id} className="border-b">
                  <td className="p-3">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">{item.sku || item.display_sku || item.product_id}</span>
                      {isCombo && (
                        <button type="button" onClick={() => onToggleCombo(item.product_id)}
                          className="ml-2 text-blue-600 hover:text-blue-800 text-xs">
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="max-w-[400px] truncate text-sm">{truncateText(item.product_name, 30)}</span>
                  </td>
                  <td className="p-3">
                    {loadingDetails[item.product_id] ? (
                      <div className="px-2 py-0 animate-spin rounded-full h-4 w-4 border-b-2 border-blue-800"></div>
                    ) : (
                      <span className={`inline-block px-2 py-1 rounded-full text-sm ${
                        stockValue === 0
                          ? 'bg-red-100 text-red-800'
                          : stockValue > 0 && stockValue < item.quantity
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {stockValue}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-sm">
                    {mode === 'view' ? (
                      <span>{item.quantity}</span>
                    ) : (
                      <input type="number" min="1" value={item.quantity}
                        onChange={(e) => onQuantityChange(index, parseInt(e.target.value) || 1)}
                        className="w-20 border border-gray-300 rounded p-1"
                      />
                    )}
                  </td>
                  <td className="p-3 text-sm">
                    {mode === 'view' || !allowPriceEdit ? (
                      <span className="font-medium">${Math.round(parseFloat(item.price)).toLocaleString('es-CO')}</span>
                    ) : (
                      <div className="w-32">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={parseFloat(item.price) || 0}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0
                            onPriceChange(index, value)
                          }}
                          mode={mode}
                          className="text-sm"
                        />
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-sm">
                    {clienteNeto && !isCombo
                      ? '0%'
                      : isCombo
                        ? '-'
                        : taxRateRaw != null && taxRateRaw !== ''
                          ? `${normalizeTaxRatePercent(taxRateRaw, 19)}%`
                          : '-'}
                  </td>
                  <td className="p-3 text-sm">
                    {mode === 'view' || !allowDiscountEdit ? (
                      <span>$ {(item.discount || 0).toLocaleString('es-CO')}</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <input 
                          type="text" 
                          value={(() => {
                            const discountType = discountTypes[item.product_id] || 'currency';
                            const subtotal = parseFloat(item.price) * item.quantity;
                            const discountInCurrency = item.discount || 0;
                            
                            if (discountType === 'percentage') {
                              // Convertir descuento en moneda a porcentaje para mostrar
                              const percentage = subtotal > 0 ? (discountInCurrency / subtotal) * 100 : 0;
                              return Math.round(percentage * 100) / 100; // Redondear a 2 decimales
                            }
                            return discountInCurrency;
                          })()}
                          onChange={(e) => {
                            const discountType = discountTypes[item.product_id] || 'currency';
                            const subtotal = parseFloat(item.price) * item.quantity;
                            let value = e.target.value;
                            
                            if (discountType === 'percentage') {
                              // Solo números y máximo 100 para porcentaje
                              value = value.replace(/[^0-9.]/g, '');
                              const numValue = parseFloat(value) || 0;
                              if (numValue > 100) value = '100';
                              
                              // Convertir porcentaje a moneda antes de guardar
                              const discountInCurrency = (subtotal * parseFloat(value || '0')) / 100;
                              onDiscountChange(index, Math.round(discountInCurrency));
                            } else {
                              // Solo números para moneda
                              value = value.replace(/[^0-9]/g, '');
                              onDiscountChange(index, parseFloat(value) || 0);
                            }
                          }}
                          placeholder={discountTypes[item.product_id] === 'percentage' ? '0-100' : '0'}
                          maxLength={discountTypes[item.product_id] === 'percentage' ? 6 : undefined}
                          className="w-24 border border-gray-300 rounded p-1 text-sm"
                        />
{/*                         <span className="text-xs text-gray-500">
                          {discountTypes[item.product_id] === 'percentage' ? '%' : '$'}
                        </span> */}
                        {(mode === 'create' || mode === 'edit') && (
                          <button
                            type="button"
                            onClick={() => {
                              const currentType = discountTypes[item.product_id] || 'currency';
                              const newType = currentType === 'currency' ? 'percentage' : 'currency';
                              setDiscountTypes(prev => ({
                                ...prev,
                                [item.product_id]: newType
                              }));
                            }}
                            className={`p-0.5 w-7.5 h-7.5 rounded border transition-colors ${
                              (discountTypes[item.product_id] || 'currency') === 'percentage'
                                ? 'bg-blue-100 border-blue-300 text-blue-700'
                                : 'bg-gray-100 border-green-300 text-gray-700 hover:bg-gray-200'
                            }`}
                            title={(discountTypes[item.product_id] || 'currency') === 'percentage' ? 'Cambiar a moneda' : 'Cambiar a porcentaje'}
                          >
                            {(discountTypes[item.product_id] || 'currency') === 'percentage' ? (
                             <ReceiptPercentIcon className="h-6 w-6 text-blue-600" />
                            
                            ) : (
                              <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
                            )}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-sm font-semibold" id={`total-${item.product_id}`}>
                    {(() => {
                      const subtotal = parseFloat(item.price) * item.quantity;
                      // El descuento siempre está en moneda en el estado
                      const discount = item.discount || 0;
                      const subtotalConDescuento = subtotal - discount;
                      if (clienteNeto) {
                        return `$${Math.round(subtotalConDescuento).toLocaleString('es-CO')}`;
                      }
                      const iva = subtotalConDescuento * ivaRateDecimal;
                      const total = subtotalConDescuento + iva;
                      
                      return `$${Math.round(total).toLocaleString('es-CO')}`;
                    })()}
                  </td>
                  {(mode === 'create' || mode === 'edit') && (
                    <td className="p-3 text-sm">
                      <button type="button" onClick={() => onRemoveProduct(index)} className="text-red-600 hover:text-red-800">
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </td>
                  )}
                </tr>
              );

              if (!isCombo || !isExpanded) {
                return mainRow;
              }

              return [
                mainRow,
                ...components.map((component: any, compIndex: number) => {
                  const componentDetails = productDetails[component.sku] || {};
                  const stockValue = getLineStock(componentDetails)
                  return (
                    <tr key={`${item.product_id}-${compIndex}-comp`} className="border-b bg-gray-50">
                      <td className="p-3 pl-12 text-sm text-gray-600">
                        <span className="text-xs">└─</span> {component.sku || ''}
                      </td>
                      <td className="p-3 text-sm text-gray-600">
                        <span className="max-w-[400px] truncate ">
                          {truncateText(component.name, 50)}
                        </span>
                      </td>
                      <td className="p-3">
                        {loadingDetails[component.sku] ? (
                          <div className="px-2 py-0 animate-spin rounded-full h-4 w-4 border-b-2 border-blue-800"></div>
                        ) : (
                          <span className={`inline-block px-2 py-1 rounded-full text-sm ${
                            stockValue === 0
                              ? 'bg-red-100 text-red-800'
                              : stockValue > 0 && stockValue < (component.quantity * item.quantity)
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {stockValue}
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-gray-600">{component.quantity * item.quantity}</td>
                      <td className="p-3 text-sm text-gray-600"></td>
                      <td className="p-3 text-sm text-gray-600">
                        {clienteNeto ? '0%' : `${normalizeTaxRatePercent(component.tax_rate, 19)}%`}
                      </td>
                      <td className="p-3 text-sm text-gray-600"></td>
                      <td className="p-3 text-sm text-gray-600">
                        {/* ${(parseFloat(component.price) * component.quantity * item.quantity * (1 + component.tax_rate)).toLocaleString('es-CO')} */}
                      </td>
                      {(mode === 'create' || mode === 'edit') && (
                        <td className="p-3">
                          <button type="button" onClick={() => onRemoveProduct(index)} className="text-red-600 hover:text-red-800">
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })
              ];
            })}
          </tbody>
        </table>
      </div>

      {lineItems && lineItems.length > 0 && (
        <div className="mt-6 flex flex-col items-end space-y-2 p-4 sm:p-6 bg-gray-50">
          {(() => {
            const totals = calculateTotalsWithShipping(lineItems);
            const dicountFinal = Math.round(parseFloat(totals.discount) || 0).toLocaleString('es-CO');
            const subtotalFinal = Math.round(parseFloat(totals.subtotal) || 0).toLocaleString('es-CO');
            // Siempre visible (también con Cliente Neto); si no hay IVA, mostrar $0
            const taxesAmount = Number.isFinite(parseFloat(totals.taxes))
              ? Math.round(parseFloat(totals.taxes))
              : 0
            const taxesFinal = taxesAmount.toLocaleString('es-CO');
            return (
              <>
                <div className="text-base">
                  Subtotal: <span className="font-semibold text-blue-800">${subtotalFinal}</span>
                </div>
                <div className="text-base">
                  Descuento: <span className="font-semibold text-blue-800">${dicountFinal}</span>
                </div>
                <div className="text-base" data-testid="totales-impuestos">
                  Impuestos: <span className="font-semibold text-blue-800">${taxesFinal}</span>
                </div>
                <div className="text-base">
                  Envío: <span className="font-semibold text-blue-800">${(parseFloat(totals.shipping) || 0).toLocaleString('es-CO')}</span>
                </div>
                <div className="text-xl font-bold border-t border-gray-300 pt-2">
                  Total: <span className="text-blue-800">${Math.round(parseFloat(totals.total) || 0).toLocaleString('es-CO')}</span>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default OrderItemsTable; 